import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];

  const isCCTVPage = location.pathname === '/' || location.pathname === '/cctv';
  const isROPage = location.pathname === '/ro';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isCCTVPage ? 'bg-black/90 border-b-2 border-green-500/30' : 'bg-blue-900/90 border-b-2 border-blue-400/30'
    } backdrop-blur-md`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isCCTVPage ? 'bg-green-500/20 border-2 border-green-500/50' : 'bg-blue-500/20 border-2 border-blue-400/50'
            }`}>
              {isCCTVPage ? (
                <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.72-2.83-.01-1.54-1.05-2.38-3.25-2.89z"/>
                  <circle cx="12" cy="12" r="3" fill="currentColor"/>
                </svg>
              ) : (
                <svg className="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                </svg>
              )}
            </div>
            <span className={`font-bold text-lg md:text-xl font-mono ${
              isCCTVPage ? 'text-green-400' : 'text-blue-400'
            }`}>
              Smart Security And RO Systems
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 font-mono ${
                isCCTVPage
                  ? 'bg-green-500/20 text-green-400 border-2 border-green-500/50'
                  : 'text-gray-300 hover:text-green-400 hover:bg-green-500/10'
              }`}
            >
              {t.navCCTV}
            </Link>
            <Link
              to="/ro"
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 font-mono ${
                isROPage
                  ? 'bg-blue-500/20 text-blue-400 border-2 border-blue-400/50'
                  : 'text-gray-300 hover:text-blue-400 hover:bg-blue-500/10'
              }`}
            >
              {t.navRO}
            </Link>
            <a
              href="#contact"
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 font-mono ${
                isCCTVPage
                  ? 'text-gray-300 hover:text-green-400 hover:bg-green-500/10'
                  : 'text-gray-300 hover:text-blue-400 hover:bg-blue-500/10'
              }`}
            >
              {t.navContact}
            </a>
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 font-mono border-2 ${
                isCCTVPage
                  ? 'border-green-500/50 text-green-400 hover:bg-green-500/20'
                  : 'border-blue-400/50 text-blue-400 hover:bg-blue-500/20'
              }`}
              title={language === 'en' ? 'हिंदी में बदलें' : 'Switch to English'}
            >
              {language === 'en' ? 'हिं' : 'EN'}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 rounded-lg ${
              isCCTVPage ? 'text-green-400' : 'text-blue-400'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className={`md:hidden py-4 border-t ${
            isCCTVPage ? 'border-green-500/30' : 'border-blue-400/30'
          }`}>
            <div className="flex flex-col space-y-2">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className={`px-4 py-3 rounded-lg font-semibold transition-all duration-300 font-mono ${
                  isCCTVPage
                    ? 'bg-green-500/20 text-green-400 border-2 border-green-500/50'
                    : 'text-gray-300'
                }`}
              >
                {t.navCCTV} {language === 'hi' ? 'स्थापना' : 'INSTALLATION'}
              </Link>
              <Link
                to="/ro"
                onClick={() => setIsOpen(false)}
                className={`px-4 py-3 rounded-lg font-semibold transition-all duration-300 font-mono ${
                  isROPage
                    ? 'bg-blue-500/20 text-blue-400 border-2 border-blue-400/50'
                    : 'text-gray-300'
                }`}
              >
                {t.navRO} {language === 'hi' ? 'शुद्धिकरण' : 'PURIFICATION'}
              </Link>
              <a
                href="#contact"
                onClick={() => setIsOpen(false)}
                className="px-4 py-3 rounded-lg font-semibold text-gray-300 font-mono"
              >
                {t.navContact}
              </a>
              {/* Language Toggle Button - Mobile */}
              <button
                onClick={toggleLanguage}
                className={`px-4 py-3 rounded-lg font-semibold transition-all duration-300 font-mono border-2 ${
                  isCCTVPage
                    ? 'border-green-500/50 text-green-400 hover:bg-green-500/20'
                    : 'border-blue-400/50 text-blue-400 hover:bg-blue-500/20'
                }`}
              >
                {language === 'en' ? 'हिंदी में बदलें' : 'Switch to English'}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;

